import React from "react";
import {
  ShieldCheck,
  BadgeCheck,
  Phone,
  Globe2,
  Clock4,
} from "lucide-react";
import { track } from "@/lib/analytics";

export const AboutPage = () => {
  return (
    <main data-testid="about-view" className="hr-paper-bg">
      <section className="max-w-2xl mx-auto px-6 sm:px-8 pt-10 sm:pt-16 pb-24">
        <header className="hr-fade-up">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#1F2E4D]">
            About us
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-[#0E7C7B] mt-3">
            Quietly, professionally, in service of Zimbabwe.
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-[#4A5568] mt-5">
            Heart Reach Counselling Services exists to make clinically-sound
            mental-health care reach the people who need it most — wherever
            they are.
          </p>
        </header>

        {/* Why telephone / remote */}
        <section className="mt-14 hr-fade-up hr-fade-up-delay-1">
          <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
            Why telephone &amp; remote counselling?
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2E4D] mt-3 leading-tight">
            We bring the service to the person.
          </h2>
          <p className="text-[15px] sm:text-base text-[#4A5568] leading-relaxed mt-5">
            We believe in bringing the service directly to the people.
            Traditional clinics require clients to travel and are restricted by
            building opening hours. By offering secure telephone and remote
            sessions, we can reach more people, support those in remote or
            underserved areas, and provide flexible care when our clients
            actually need it.
          </p>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { Icon: Phone, title: "No internet data required" },
              { Icon: Globe2, title: "Reaches remote and underserved areas" },
              { Icon: Clock4, title: "Flexible — when the client needs it" },
            ].map(({ Icon, title }) => (
              <li
                key={title}
                className="bg-white rounded-2xl border border-[#E8E2D4] p-5 flex items-start gap-3"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E0EBEB] shrink-0">
                  <Icon className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                </span>
                <p className="text-[15px] font-serif text-[#1F2E4D] leading-snug">
                  {title}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <hr className="hr-divider my-14" />

        {/* Credentials */}
        <section className="hr-fade-up hr-fade-up-delay-2">
          <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
            Our credentials
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2E4D] mt-3 leading-tight">
            Trained, vetted, and clinically accountable.
          </h2>
          <p className="text-[15px] sm:text-base text-[#4A5568] leading-relaxed mt-5">
            All our counsellors are fully trained, strictly vetted with
            up-to-date police clearances, and officially registered with the{" "}
            <span className="font-semibold text-[#1F2E4D]">
              Allied Health Practitioners Council of Zimbabwe (AHPCZ)
            </span>
            . Clinical safety and confidentiality are our highest priorities.
          </p>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { Icon: BadgeCheck, title: "Fully trained" },
              { Icon: ShieldCheck, title: "Police-cleared" },
              { Icon: BadgeCheck, title: "AHPCZ registered" },
            ].map(({ Icon, title }) => (
              <li
                key={title}
                className="bg-white rounded-2xl border border-[#E8E2D4] p-5 flex items-start gap-3"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E0EBEB] shrink-0">
                  <Icon className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                </span>
                <p className="text-[15px] font-serif text-[#1F2E4D] leading-snug">
                  {title}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <hr className="hr-divider my-14" />

        {/* Who We Are + Certificate */}
        <section className="hr-fade-up hr-fade-up-delay-3">
          <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
            Who we are
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2E4D] mt-3 leading-tight">
            A registered Zimbabwean business — operating since January 2025.
          </h2>
          <p className="text-[15px] sm:text-base text-[#4A5568] leading-relaxed mt-5">
            Heart Reach Counselling Services PBC is a fully registered
            business in Zimbabwe, successfully operating since January 2025.
          </p>

          {/* Embedded Certificate of Registration */}
          <figure
            data-testid="about-certificate-card"
            className="mt-8 bg-white rounded-2xl border border-[#E8E2D4] hr-card-shadow p-3 sm:p-4"
            onContextMenu={(e) => e.preventDefault()}
          >
            <img
              src="/about/certificate.jpg"
              alt="Heart Reach Counselling Services PBC — Certificate of Registration (Zimbabwe)"
              loading="lazy"
              decoding="async"
              draggable="false"
              data-testid="about-certificate-image"
              className="w-full h-auto rounded-xl select-none pointer-events-none"
            />
            <figcaption className="text-xs text-[#4A5568] mt-3 px-1 leading-relaxed">
              Heart Reach Counselling Services PBC — Certificate of
              Registration, issued by the Republic of Zimbabwe.
            </figcaption>
          </figure>
        </section>
      </section>
    </main>
  );
};

export default AboutPage;
